import { ArrowLeft, ExternalLink, Loader2 } from "lucide-react";
import { Link, useParams } from "react-router";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { blockImageUrl } from "./api";
import { BlockInstall } from "./block-install";
import { BlockSpec } from "./block-spec";
import { useBlock } from "./hooks";
import { accentFor } from "./types";

export function BlockDetailPage() {
  const { slug = "" } = useParams();
  const { data: block, isPending, isError, error } = useBlock(slug);

  if (isPending) {
    return (
      <div className="flex items-center gap-2 py-24 text-sm text-ink-400">
        <Loader2 className="animate-spin" size={16} />
        Loading…
      </div>
    );
  }

  if (isError) {
    return (
      <div className="space-y-4 py-16">
        <p className="text-sm text-ink-200">{error.message}</p>
        <BackLink />
      </div>
    );
  }

  const accent = accentFor(block.category);

  return (
    <article className="space-y-10 pb-16">
      <BackLink />

      <header className="grid gap-8 lg:grid-cols-[1fr_22rem] lg:items-start">
        <div className="order-2 space-y-4 lg:order-1">
          <div className="flex flex-wrap items-center gap-2">
            <span
              className="rounded-full px-2.5 py-0.5 text-xs font-medium"
              style={{
                background: `color-mix(in oklab, ${accent} 16%, transparent)`,
                color: accent,
              }}
            >
              {block.category}
            </span>
            <span className="font-mono text-xs text-ink-400">v{block.version}</span>
          </div>
          <h1 className="text-3xl font-semibold tracking-tight text-ink-50 sm:text-4xl">
            {block.name}
          </h1>
          <p className="text-lg leading-relaxed text-ink-400">{block.tagline}</p>

          {block.reference ? (
            <a
              href={block.reference.url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 text-sm text-ink-200 underline-offset-4 hover:underline"
            >
              Used in {block.reference.label}
              <ExternalLink size={13} />
            </a>
          ) : (
            /* Stated rather than hidden: a block is useful before anyone has
               shipped it, and silence here would read as an oversight. */
            <p className="text-xs text-ink-400">
              No reference implementation yet — this block hasn&apos;t shipped in a
              production app.
            </p>
          )}
        </div>

        {block.has_image && (
          <div className="order-1 overflow-hidden rounded-xl border border-ink-700 bg-ink-950 lg:order-2">
            <img src={blockImageUrl(block.slug)} alt="" className="w-full" />
          </div>
        )}
      </header>

      <BlockInstall block={block} />

      <section className="prose-block max-w-3xl text-[0.9375rem] text-ink-200">
        <Markdown remarkPlugins={[remarkGfm]}>{block.description}</Markdown>
      </section>

      <BlockSpec block={block} />
    </article>
  );
}

function BackLink() {
  return (
    <Link
      to="/"
      className="inline-flex items-center gap-1.5 text-sm text-ink-400 transition-colors hover:text-ink-200"
    >
      <ArrowLeft size={14} />
      All blocks
    </Link>
  );
}
